import type { DOMEvent } from '../types/html-event-target'

type Id = string | number;

/**
 * Interface for function parameters `setValue`
 * id = Product Id
 * change = optional method to change the current value (typically by 1 either side)
 */
interface SetValueParams {
  id: Id;
  change?: (number) => number | undefined;
}

declare const basketQuantities: number[][]

/**
 * Used for editing quantity on product listing pages. Usually has + and - buttons to
 * increment and decrement quantity before adding to basket.
 */
export default (): void => {
  /**
   * Array of all elements that can alter the quantity of a product. Typically a button
   * that will alter by 1 - either adding or subtracting
   */
  const actions: HTMLElement[] = Array.from(document.querySelectorAll('.product-qty-change'))
  const addButtons: HTMLElement[] = Array.from(document.querySelectorAll('.btn-cta.product-cta'))

  const qtyInputs: HTMLInputElement[] = Array.from(document.querySelectorAll('.product-qty-input'))

  /**
   * populates the quantities of products on the page, instead of relying on ruby that will break
   * with caching
   */
  function populateQuantities (): void {
    qtyInputs.forEach((input) => {
      const { productId } = input.dataset

      basketQuantities.forEach((quantity: number[]) => {
        const [givenId, givenValue] = quantity

        if (String(givenId) === String(productId)) {
          // Check the data-filled property as we only want to set a quantity box
          // once.
          if (!input.hasAttribute('data-filled')) {
            input.value = givenValue.toString()
          }
        }
      })

      // We've filled, or at least considered filling, this quantity box so mark
      // it as such so we skip it in future.
      input.setAttribute('data-filled', '1')
    })
  }

  /**
   * Finds the actual quantity number input for a given product
   * @param productId The product whose quantity you want to change
   */
  const findQuantityInput = (productId: Id): HTMLInputElement => (
    document.querySelector(`input[name="qty[${String(productId)}]"]`)
  )

  /**
   * Will replace 0 with '', or just coerce the number into a string
   * @param value The HTMLInput value
   */
  function stringifyZero (value: number): string {
    if (value === 0 || value < 0) {
      return ''
    }
    return String(value)
  }

  /**
   * Sets value according to optional given callback. Used to stringify zeroes on first launch.
   * @param {SetValueParams} params See SetValueParams Interface.
   * @example
   * ```
   * // Set product of id 1 to have a visible quantity of what it is currently + 1
   * setValue(1, (currentValue) => currentValue + 1 )
   * ```
   * @see `stringifyZero`
   */
  function setValue (params: SetValueParams): void {
    const { id, change } = params
    const quantityInput = findQuantityInput(id)
    const { value } = quantityInput
    let result: number

    if (change) {
      result = change(Number(value))
    } else {
      result = Number(value)
    }

    quantityInput.value = stringifyZero(result)
    quantityInput.dispatchEvent(new Event('change', { bubbles: true }))
  }

  function getProductId (element: HTMLElement): string {
    if (element.hasAttribute('data-qty-decrement')) {
      return element.dataset.qtyDecrement
    }
    return element.dataset.qtyIncrement
  }

  /**
   * Loop through all increment/decrement buttons and adds an event listener.
   * Each listener checks if it will decrement or increment and act accordingly
   */
  actions.forEach((action): void => {
    // Ensure all zeroed-values are '';
    setValue({ id: getProductId(action) })

    action.addEventListener('click', (event: DOMEvent<HTMLElement>): void => {
      const { target } = event
      const { dataset } = target

      // Decrement so decrease currentValue by 1
      if (target.hasAttribute('data-qty-decrement')) {
        setValue({
          id: dataset.qtyDecrement,
          change: (currentValue: number) => (
            currentValue - 1
          )
        })
        // Increment so do the opposite
      } else if (target.hasAttribute('data-qty-increment')) {
        setValue({
          id: dataset.qtyIncrement,
          change: (currentValue: number) => (
            currentValue + 1
          )
        })
      }
    })
  })

  /**
   * Add event listener to each "addButton"
   * Update the text value and then reset after a delay of 1 second
   * Only do this if the value is valid (that is, not 0 or empty)
   */
  addButtons.forEach((button): void => {
    button.addEventListener('click', (): void => {
      const buttonText = button.querySelector('span')
      const productId = button.getAttribute('data-product-id')

      const quantityInput = findQuantityInput(productId)
      if (quantityInput === null) return
      const { value } = quantityInput

      if (!['0', ''].includes(value)) {
        buttonText.innerHTML = 'Added'
        setTimeout(() => {
          buttonText.innerHTML = 'Add to basket'
        }, 1000)
      }
    })
  })

  populateQuantities()
}
